import React, { useEffect, useState } from 'react';
import Calendar from 'react-calendar';
import moment from 'moment';

import {Grid, Typography, CardContent, IconButton, Card } from '@material-ui/core';
import { Stack, Switch, Tooltip, Chip } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles, withStyles, Theme, createStyles } from '@material-ui/core/styles';
import FiberManualRecordSharpIcon from '@material-ui/icons/FiberManualRecordSharp';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_PRIMARY_LIGHT,
  COLOR_GREEN,
  COLOR_RED_LIGHT,
  DARK_GREY,
} from 'src/const';
import { formatCalendarDayName } from 'src/utils/functions';
import { callApi } from 'src/core/callApi';
import 'react-calendar/dist/Calendar.css';
import { InfoTooltip } from 'src/App/HOC/Tooltips';
import { TooltipCard, UserListingInfoBar } from 'src/App/HOC';
import '../../Dashboard.scss';
import '../Styles/Calendar.scss';
import { CalendarDashboardPropTypes } from 'src/interfaces/iproptypes';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    calendarContainer: {
      width: '100%',
      background: '#ffffffbb',
      padding: 0,
      boxShadow: 'none',
    },
    gaugeBar: {
      marginTop: 20,
      width: '100%',
    },
    calendar: {
      width: '100%',
      background: '#ffffffbb',
      mixBlendMode: 'multiply',
      border: 0,
    },
    calendarTiles: {
      height: 100,
      position: 'relative',
      top: 0,
      left: 0,
      fontSize: '16px',
      fontWeignt: '700',
      padding: '5px 10px',
      border: '1px solid black',
    },
    customButton: {
      background: COLOR_PRIMARY_DARK,
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      borderRadius: 5,
      border: 0,
      color: 'white',
      height: 40,
      padding: '0 15px',
      boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
      '&:hover': {
        background: COLOR_PRIMARY_LIGHT,
      },
    },
    iconButton: {
      padding: 0,
    },
    airbnbLogo: {
      height: 30,
      marginRight: 10,
      marginLeft: 20,
    },
  })
);

const theme = createTheme({
  palette: {
    primary: {
      main: COLOR_PRIMARY,
    },
  },
});

const CalendarDashboard = ({
  listingPricingRecommendation,
  getListingPricingRecommendation,
  calendarListingID,
  airbnbLoggedIn,
}: CalendarDashboardPropTypes) => {
  const classes = useStyles();
  const pricingStratObj = {
    recommended: 'Recommended',
    conservative: 'Conservative',
    aggressive: 'Aggressive',
  };

  const matches = useMediaQuery(theme.breakpoints.down(769));

  // const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [isPricingModeChanging, setIsPricingModeChanging] = useState<boolean>(false);
  const [localSyncStatus, setLocalSyncStatus] = useState<boolean>(false);
  const [lastUpdatedString, setLastUpdatedString] = useState('');

  const [pricingStrategy, setPricingStrategy] = useState<
    'recommended' | 'conservative' | 'aggressive'
  >('recommended');

  const calendarData = listingPricingRecommendation.rates;
  const endingDate = listingPricingRecommendation.ending_date;
  const listingDetails = listingPricingRecommendation.listing_details;

  useEffect(() => {
    if (listingPricingRecommendation) {
      const syncStatus = listingDetails.is_dynamic_pricing_on;
      setLocalSyncStatus(syncStatus === 't' ? true : false);

      const lastUpdated = listingDetails.last_updated;
      const momentUTC = moment.utc(lastUpdated);
      const momentLocal = momentUTC.local().format('LLLL');
      setLastUpdatedString(momentLocal);
      setPricingStrategy('recommended');
    }
  }, [listingPricingRecommendation]);

  const handleSyncStatusChange = event => {
    const setDynamicPricingStatusURL =
      'https://4pcbx2lwyi.execute-api.us-east-2.amazonaws.com/default/setDynamicPricingStatus';
    setLocalSyncStatus(e => {
      const syncStatus = e ? 'f' : 't';
      const config = {
        queryParams: {
          listingID: calendarListingID,
          syncStatus: syncStatus,
        },
      };
      callApi(setDynamicPricingStatusURL, config, false);
      if (e === false) {
        // getListingPricingRecommendation(calendarListingID, true);
      }
      return !e;
    });
  };

  const handlePricingStratChange = async (mode: string) => {
    setIsPricingModeChanging(true);
    const setPricingStratURL =
      'https://gyecm3rhw7.execute-api.us-east-2.amazonaws.com/default/setPricingStrategy';
    const config = {
      queryParams: {
        listingID: calendarListingID,
        pricingStrategy: mode,
      },
    };
    await callApi(setPricingStratURL, config, false);
    // await getListingPricingRecommendation(calendarListingID, false);
    // await getListingPricingRecommendation(calendarListingID, localSyncStatus);
    setIsPricingModeChanging(false);
  };

  const PricingModeChangingUI = () => {
    return (
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: theme => theme.zIndex.drawer + 1,
        }}
        open={isPricingModeChanging}
      >
        <CircularProgress color="inherit" size={50} thickness={2} />
      </Backdrop>
    );
  };

  return (
    <Grid container direction="column" justify="flex-start" alignItems="flex-start" spacing={4}>
      <Grid item container direction="row-reverse" justify="space-between">
        <Grid item container direction="column" alignItems="flex-end" xs={3}>
          <InfoTooltip
            title={
              <TooltipCard
                content={
                  localSyncStatus
                    ? 'Your dynamic pricing is on. Bright Pricing recommended rates will be automatically pushed to your Airbnb listing.'
                    : 'Your dynamic pricing is off. Turn it on for Bright Pricing recommended rates to be automatically pushed to your Airbnb listing.'
                }
              />
            }
          >
            <Grid item container direction="row" justify="flex-end" alignItems="flex-start">
              <ThemeProvider theme={theme}>
                <Switch
                  checked={localSyncStatus ? true : false}
                  onChange={handleSyncStatusChange}
                  style={{ marginRight: 10 }}
                  disabled={airbnbLoggedIn === false}
                />
              </ThemeProvider>

              <Typography style={{ fontSize: 20, fontWeight: 500 }}>
                Syncing: {localSyncStatus ? 'ON' : 'OFF'}
                <IconButton className={classes.iconButton}>
                  <FiberManualRecordSharpIcon
                    style={{
                      color: localSyncStatus ? COLOR_GREEN : COLOR_RED_LIGHT,
                      marginBottom: 4,
                    }}
                  />
                </IconButton>
              </Typography>
            </Grid>
          </InfoTooltip>

          <Typography style={{ fontSize: 18, fontWeight: 500 }} color="textSecondary">
            Updated at {lastUpdatedString}
          </Typography>
        </Grid>

        <Grid item container direction="row" justifyContent="flex-start" alignItems="center" xs={8}>
          <UserListingInfoBar listing={listingDetails} isUserListing={true} />
        </Grid>
      </Grid>

      <div
        style={{
          width: '95%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginLeft: '2.5%',
          marginRight: '2.5%',
          marginTop: '20px',
          marginBottom: '10px',
        }}
      >
        <Typography variant="h5" style={{ color: '#2e2e2e', fontWeight: 'bold' }}>
          Pricing Mode
        </Typography>
        <Stack spacing={2} direction="row" justifyContent="flex-end">
          <Chip
            label="Recomended"
            variant={pricingStrategy === 'recommended' ? 'filled' : 'outlined'}
            clickable={true}
            onClick={() => {
              setPricingStrategy('recommended');
              handlePricingStratChange(pricingStratObj['recommended']);
            }}
            sx={{
              '& span': {
                overflow: 'visible',
              },
              overflow: 'hidden',
              minHeight: '35px',
              borderColor: '#71FF64bb',
              background: pricingStrategy === 'recommended' ? '#71FF64bb' : '#fff',
              '&:hover': {
                background: '#71FF6455',
              },
            }}
            disabled={airbnbLoggedIn === false}
          />
          <Chip
            label="Conservative"
            variant={pricingStrategy === 'conservative' ? 'filled' : 'outlined'}
            clickable={true}
            onClick={() => {
              setPricingStrategy('conservative');
              handlePricingStratChange(pricingStratObj['conservative']);
            }}
            sx={{
              '& span': {
                overflow: 'visible',
              },
              overflow: 'hidden',
              minHeight: '35px',
              borderColor: '#68F1FAbb',
              background: pricingStrategy === 'conservative' ? '#68F1FAbb' : '#fff',
              '&:hover': {
                background: '#68F1FA55',
              },
            }}
            disabled={airbnbLoggedIn === false}
          />
          <Chip
            label="Aggressive"
            variant={pricingStrategy === 'aggressive' ? 'filled' : 'outlined'}
            clickable={true}
            onClick={() => {
              setPricingStrategy('aggressive');
              handlePricingStratChange(pricingStratObj['aggressive']);
            }}
            sx={{
              '& span': {
                overflow: 'visible',
              },
              overflow: 'hidden',
              minHeight: '35px',
              borderColor: '#E92929bb',
              background: pricingStrategy === 'aggressive' ? '#E92929bb' : '#fff',
              '&:hover': {
                background: '#E9292955',
              },
            }}
            disabled={airbnbLoggedIn === false}
          />
        </Stack>
      </div>

      <PricingModeChangingUI />

      <Grid item style={{ width: '100%', filter: 'drop-shadow(0 0 5px rgba(0, 0, 0, 0.1))' }}>
        <Card className={classes.calendarContainer}>
          <CardContent style={{ position: 'relative', background: 'transparent' }}>
            <div className="bg__green"></div>
            <div className="bg__blue"></div>
            <Grid>
              <Calendar
                calendarType="US"
                className={classes.calendar}
                defaultView="month"
                formatShortWeekday={(_, date) => formatCalendarDayName(date, matches)}
                minDate={new Date()}
                maxDate={new Date(endingDate)}
                minDetail="year"
                next2Label={null}
                prev2Label={null}
                showNeighboringMonth={false}
                tileClassName={classes.calendarTiles}
                tileContent={({ activeStartDate, date, view }) => {
                  const offset = date.getTimezoneOffset();
                  let dateString = new Date(date.getTime() - offset * 60 * 1000)
                    .toISOString()
                    .split('T')[0];

                  if (
                    new Date(dateString) > new Date() &&
                    +new Date(dateString) <= +new Date(endingDate) &&
                    view === 'month'
                  ) {
                    return (
                      <>
                        <InfoTooltip
                          title={
                            <Card
                              style={{
                                boxShadow: 'none',
                                filter: 'drop-shadow(0 0 10px rgba(0, 0, 0, 0.2))',
                              }}
                            >
                              <CardContent style={{ background: '#fff', position: 'relative' }}>
                                <div
                                  style={{
                                    background: '#9EFF96',
                                    width: '150px',
                                    height: '150px',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    right: '-70px',
                                    top: 0,
                                    filter: 'blur(50px)',
                                  }}
                                />
                                <Grid container direction="column" alignItems="flex-start">
                                  <div style={{ fontWeight: 500, zIndex: 2, width: '100%' }}>
                                    <div style={{ marginBottom: '10px' }}>
                                      <GetColorRating
                                        demand={calendarData[`${dateString}`]['occupancyRate']}
                                        size={'medium'}
                                      />
                                    </div>
                                    <div className="calendar__tooltip__text">
                                      <Typography
                                        variant="body2"
                                        className="calendar__tooltip__left"
                                      >
                                        Bright Pricing Rate:{' '}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        className="calendar__tooltip__right"
                                      >
                                        {calendarData[`${dateString}`]['recommendedRate']}
                                      </Typography>
                                    </div>

                                    <div className="calendar__tooltip__text">
                                      <Typography
                                        variant="body2"
                                        className="calendar__tooltip__left"
                                      >
                                        Your Current Rate:{' '}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        className="calendar__tooltip__right"
                                      >
                                        {calendarData[`${dateString}`]['userRate']}
                                      </Typography>
                                    </div>

                                    <div className="calendar__tooltip__text">
                                      <Typography
                                        variant="body2"
                                        className="calendar__tooltip__left"
                                      >
                                        Neighborhood Occupancy Rate:{' '}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        className="calendar__tooltip__right"
                                      >
                                        {calendarData[`${dateString}`]['occupancyRate']}%
                                      </Typography>
                                    </div>

                                    <div className="calendar__tooltip__text">
                                      <Typography
                                        variant="body2"
                                        className="calendar__tooltip__left"
                                      >
                                        Currency:{' '}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        className="calendar__tooltip__right"
                                      >
                                        {calendarData[`${dateString}`]['currency']}
                                      </Typography>
                                    </div>
                                  </div>
                                </Grid>
                              </CardContent>
                            </Card>
                          }
                        >
                          <Grid
                            container
                            direction="column"
                            justify="flex-end"
                            alignItems="flex-end"
                            style={{ height: 60 }}
                          >
                            <Typography
                              variant="caption"
                              style={{
                                color: COLOR_PRIMARY_DARK,
                                fontWeight: 'bold',
                                fontSize: '16px',
                              }}
                            >
                              {calendarData[`${dateString}`]['recommendedRate']}
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{
                                textDecoration: 'line-through',
                                color: DARK_GREY,
                                fontWeight: 'bold',
                              }}
                            >
                              {calendarData[`${dateString}`]['userRate']}
                            </Typography>
                          </Grid>
                        </InfoTooltip>
                        <Tooltip
                          title={
                            <GetColorRating
                              demand={calendarData[`${dateString}`]['occupancyRate']}
                              size="small"
                            />
                          }
                          arrow
                        >
                          <div className="calendar__index__color__dot">
                            <GetColorRating
                              text={false}
                              demand={calendarData[`${dateString}`]['occupancyRate']}
                              size="medium"
                            />
                          </div>
                        </Tooltip>
                      </>
                    );
                  } else {
                    return null;
                  }
                }}
              />
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid
        item
        xs={12}
        style={{ margin: 'auto', marginBottom: '100px', marginTop: '30px' }}
        className="calendar__demand__index__bar"
      >
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={3}>
          <GetColorRating demand={0} />
          <GetColorRating demand={10} />
          <GetColorRating demand={40} />
          <GetColorRating demand={60} />
          <GetColorRating demand={80} />
        </Stack>
      </Grid>
    </Grid>
  );
};

const GetColorRating = ({
  demand,
  text = true,
  size = 'normal',
}: {
  demand: number;
  text?: boolean;
  size?: 'normal' | 'small' | 'medium';
}) => {
  const colorArray = [
    { color: '#EFEFEF', message: 'No Demand' },
    { color: '#BFFFB9', message: 'Low Demand' },
    { color: '#71FF64', message: 'Normal Demand' },
    { color: '#86F8FF', message: 'Good Demand' },
    { color: '#FF85F3', message: 'High Demand' },
  ];

  //color and text of the color dots
  let demandIndex: number = 0;

  if (demand >= 1 && demand < 30) {
    demandIndex = 1;
  } else if (demand >= 30 && demand < 50) {
    demandIndex = 2;
  } else if (demand >= 50 && demand < 70) {
    demandIndex = 3;
  } else if (demand >= 70 && demand <= 100) {
    demandIndex = 4;
  }

  // size of the color dots
  let sizeValue: number = 20;

  if (size === 'small') {
    sizeValue = 10;
  } else if (size === 'medium') {
    sizeValue = 15;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <div
        style={{
          height: sizeValue,
          width: sizeValue,
          minHeight: sizeValue,
          minWidth: sizeValue,
          backgroundColor: colorArray[demandIndex].color,
          borderRadius: '50%',
        }}
      />
      {text && (
        <Typography variant="body2" component="p">
          {colorArray[demandIndex].message}
        </Typography>
      )}
    </Stack>
  );
};

export default CalendarDashboard;

//--------------------------------------------------------
//color rating bar data
//--------------------------------------------------------
// #EFEFEF no demand        0 -> < 1 %
// #BFFFB9 low demand       1 -> < 30 %
// #71FF64 normal demand    30 -> < 50 %
// #86F8FF good demand      50 -> < 70 %
// #FF85F3 high demand      70 -> 100 %
//--------------------------------------------------------
